import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Users, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

// Live chat wired to the real backend:
//   GET  /api/events/{eventId}/messages  (polled)
//   POST /api/events/{eventId}/messages  (auth required — 401 handled)
export function LiveChatPlaceholder({ eventId, viewers }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    try {
      const list = await apiClient.getEventMessages(eventId);
      setMessages(list);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (!eventId) {
      setLoading(false);
      return;
    }
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [eventId, load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    if (!user) {
      toast.error("Log in to join the chat", {
        description: "You need an account to send messages.",
        action: { label: "Log in", onClick: () => navigate("/login") },
      });
      return;
    }
    setSending(true);
    try {
      const msg = await apiClient.postEventMessage(eventId, text);
      setMessages((prev) => [...prev, msg].slice(-50));
      setDraft("");
    } catch (err) {
      if (err.code === 401) {
        toast.error("Session expired", { description: "Please log in again to chat." });
        navigate("/login");
      } else {
        toast.error("Couldn't send message", { description: "Please try again." });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="live-chat"
      className="flex h-full flex-col rounded-2xl border border-white/10 bg-[#111f38]"
    >
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <span className="font-heading text-sm font-bold text-white">Live chat</span>
        <span className="flex items-center gap-1.5 text-xs text-neutral-400">
          <Users className="h-3.5 w-3.5" />
          {viewers?.toLocaleString?.() ?? "—"} watching
        </span>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
        style={{ scrollbarWidth: "thin" }}
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-neutral-500">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : error ? (
          <div
            data-testid="live-chat-error"
            className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-neutral-400"
          >
            <AlertCircle className="h-6 w-6 text-neutral-500" />
            Chat is unavailable right now.
          </div>
        ) : messages.length === 0 ? (
          <div
            data-testid="live-chat-empty"
            className="flex h-full items-center justify-center text-center text-sm text-neutral-500"
          >
            No messages yet — be the first to say hi.
          </div>
        ) : (
          messages.map((m) => {
            const mine = user && m.userId === user.id;
            return (
              <div key={m.id} className="text-sm leading-snug">
                <span className={mine ? "font-semibold text-brand" : "font-semibold text-neutral-300"}>
                  {mine ? "You" : m.user}
                </span>{" "}
                <span className="text-neutral-100">{m.text}</span>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={send} className="flex items-center gap-2 border-t border-white/10 p-3">
        <input
          data-testid="live-chat-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={user ? "Say something…" : "Log in to chat…"}
          className="w-full rounded-full bg-white/5 px-4 py-2 text-sm text-white outline-none placeholder:text-neutral-500 focus:bg-white/10"
        />
        <button
          type="submit"
          data-testid="live-chat-send"
          disabled={sending}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-colors hover:bg-brand-hover disabled:opacity-60"
          aria-label="Send"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}

export default LiveChatPlaceholder;
